import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Download, Pencil, Trash2, Loader2, RefreshCw } from "lucide-react";

const STATUS_CONFIG = {
  new: { label: "Nouveau", color: "bg-blue-100 text-blue-700" },
  contacted: { label: "Contacte", color: "bg-amber-100 text-amber-700" },
  won: { label: "Gagne", color: "bg-emerald-100 text-emerald-700" },
  lost: { label: "Perdu", color: "bg-red-100 text-red-700" },
};

const URGENCE_CONFIG = {
  faible: { label: "Faible", color: "bg-slate-100 text-slate-600" },
  moyen: { label: "Moyen", color: "bg-amber-100 text-amber-700" },
  urgent: { label: "Urgent", color: "bg-red-100 text-red-700" },
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVille, setFilterVille] = useState("all");
  const [filterService, setFilterService] = useState("all");
  const [villes, setVilles] = useState([]);
  const [services, setServices] = useState([]);
  const [editLead, setEditLead] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterVille !== "all") params.ville = filterVille;
      if (filterService !== "all") params.service = filterService;
      const res = await api.get(`/leads`, { params });
      setLeads(res.data);
    } catch (err) {
      toast.error("Erreur chargement des leads");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterVille, filterService]);

  useEffect(() => {
    Promise.all([
      api.get(`/villes`),
      api.get(`/services`),
    ]).then(([v, s]) => {
      setVilles(v.data);
      setServices(s.data);
    });
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleEdit = (lead) => {
    setEditLead(lead);
    setEditData({ status: lead.status, urgence: lead.urgence, valeur_estimee: lead.valeur_estimee || 0 });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/leads/${editLead.id}`, editData);
      toast.success("Lead mis a jour");
      setEditLead(null);
      fetchLeads();
    } catch (err) {
      toast.error("Erreur lors de la mise a jour");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads_nettoyage_expert.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Supprimer le lead "${lead.nom}" ?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead supprimé");
      setLeads(prev => prev.filter(l => l.id !== lead.id));
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleSyncLoomy = async () => {
    const unsyncedCount = leads.filter(l => l.loomy_synced !== true).length;
    if (unsyncedCount === 0) { toast.info("Tous les leads sont déjà synchronisés"); return; }
    if (!window.confirm(`Envoyer ${unsyncedCount} lead(s) non synchronisé(s) vers Loomy CRM ?`)) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/loomy/sync-all");
      toast.success(data.message);
      fetchLeads();
    } catch {
      toast.error("Erreur lors de la synchronisation");
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">{leads.length} lead(s) au total</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSyncLoomy} disabled={syncing} variant="outline" className="gap-2" data-testid="sync-loomy-button">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {syncing ? "Sync en cours..." : "Sync Loomy CRM"}
          </Button>
          <Button onClick={handleExport} className="gap-2 bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="export-csv-button">
            <Download className="w-4 h-4" />
            Exporter CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3" data-testid="leads-filters">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]" data-testid="filter-status">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="new">Nouveau</SelectItem>
                <SelectItem value="contacted">Contacte</SelectItem>
                <SelectItem value="won">Gagne</SelectItem>
                <SelectItem value="lost">Perdu</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterVille} onValueChange={setFilterVille}>
              <SelectTrigger className="w-[160px]" data-testid="filter-ville">
                <SelectValue placeholder="Ville" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les villes</SelectItem>
                {villes.map((v) => (
                  <SelectItem key={v.slug} value={v.name}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterService} onValueChange={setFilterService}>
              <SelectTrigger className="w-[180px]" data-testid="filter-service">
                <SelectValue placeholder="Service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les services</SelectItem>
                {services.map((s) => (
                  <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#336DBF]" />
            </div>
          ) : leads.length === 0 ? (
            <p className="text-sm text-slate-400 py-12 text-center">Aucun lead trouve</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nom</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Tel</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Service</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Ville</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Statut</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Urgence</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">CRM</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id} className="hover:bg-slate-50/50" data-testid={`lead-row-${lead.id}`}>
                    <TableCell className="text-sm font-medium text-slate-900">{lead.nom}</TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.email}</TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.telephone}</TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.service}</TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.ville}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={STATUS_CONFIG[lead.status]?.color || ""}>
                        {STATUS_CONFIG[lead.status]?.label || lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={URGENCE_CONFIG[lead.urgence]?.color || ""}>
                        {URGENCE_CONFIG[lead.urgence]?.label || lead.urgence}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString("fr-CH") : "-"}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`lead-crm-${lead.id}`}>
                      {lead.loomy_synced === true && <span className="text-emerald-600" title="Envoyé à Loomy CRM">&#10003;</span>}
                      {lead.loomy_synced === false && <span className="text-red-500" title="Échec envoi Loomy">&#10007;</span>}
                      {lead.loomy_synced == null && <span className="text-slate-400" title="En attente">&#8943;</span>}
                    </TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(lead)} data-testid={`edit-lead-${lead.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(lead)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-lead-${lead.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editLead} onOpenChange={() => setEditLead(null)}>
        <DialogContent className="max-w-md" data-testid="edit-lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le lead</DialogTitle>
            <DialogDescription>
              {editLead?.nom} — {editLead?.service}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Statut</label>
              <Select value={editData.status} onValueChange={(v) => setEditData((p) => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="edit-lead-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Nouveau</SelectItem>
                  <SelectItem value="contacted">Contacte</SelectItem>
                  <SelectItem value="won">Gagne</SelectItem>
                  <SelectItem value="lost">Perdu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Urgence</label>
              <Select value={editData.urgence} onValueChange={(v) => setEditData((p) => ({ ...p, urgence: v }))}>
                <SelectTrigger data-testid="edit-lead-urgence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="faible">Faible</SelectItem>
                  <SelectItem value="moyen">Moyen</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Valeur estimée (CHF)</label>
              <Input
                type="number"
                value={editData.valeur_estimee}
                onChange={(e) => setEditData((p) => ({ ...p, valeur_estimee: parseFloat(e.target.value) || 0 }))}
                data-testid="edit-lead-valeur"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLead(null)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="save-lead-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
